import type { ReactElement } from 'react'
import { type UnsavedRole } from '@terros/common'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { isNotEmpty, useForm } from '@mantine/form'
import { Group } from '@mantine/core'
import { useToastError } from '@hooks'
import { useAddRole, useProfile, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, PermissionCheckWrapper } from '@components'
import { RoleForm } from './RoleForm'

function AddRolePage(): ReactElement {
  const { company, error, isLoading } = useProfile()
  const { t } = useTranslation()

  if (!company) {
    return <NotReady data={company} error={error} id='company' isLoading={isLoading} />
  }

  return (
    <Page>
      <PermissionCheckWrapper navigationTo='parent' permissionType='role:manage' targetId={company.companyId}>
        <Page.Heading title={t('Add Role')} />
        <RoleAddForm />
      </PermissionCheckWrapper>
    </Page>
  )
}

const RoleAddForm = (): ReactElement => {
  const navigate = useNavigate()
  const { add, isAdding, error } = useAddRole()
  const { t } = useTranslation()
  useToastError(error)

  const form = useForm<UnsavedRole>({
    initialValues: {
      name: '',
      description: '',
    },
    validate: {
      name: isNotEmpty(t('Team name is required')),
    },
  })

  const { onSubmit, isValid } = form

  const submit = async (values: UnsavedRole): Promise<void> => {
    const role = await add(values)

    navigate({ to: `/settings/role/${role.roleId}` })
  }

  return (
    <form className='w-full' onSubmit={onSubmit(submit)}>
      <Page.Content>
        <RoleForm form={form} />
        <Group justify='flex-end'>
          <ButtonAdd disabled={!isValid()} loading={isAdding} type='submit'>
            {t('Add role')}
          </ButtonAdd>
        </Group>
      </Page.Content>
    </form>
  )
}

export const Route = createFileRoute('/settings/role/add/')({
  component: AddRolePage,
})
